import { type TypedDocumentNode } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { getVideoLeadThumbnail } from '../../api'
import { getLiveImageSizes } from '../../config'

// getVideoLeadThumbnail() lives in untyped JS (api/brightspot/video.js), so Apollo v4 infers
// `{}` for its result. Attach the shape to the document rather than passing a TData generic to
// useQuery — v4 deprecates the generic in favor of inference from a TypedDocumentNode.
type VideoLeadThumbnailData = {
  VideoDetail?: {
    thumbnail?: { custom?: { image?: { src?: string } } }
  }
}

/**
 * Fetches a video's poster at hero/lead scale by ID, for components that render a video's
 * thumbnail at full-width or half-viewport scale (e.g. MediaPromo) rather than card scale.
 * Returns undefined while loading or if unavailable, so callers should fall back to their
 * existing (card-sized) thumbnail source until this resolves.
 */
export default function useLeadVideoThumbnail(videoId?: string): string | undefined {
  const { leadThumbnailImage } = getLiveImageSizes()
  const query = getVideoLeadThumbnail() as TypedDocumentNode<VideoLeadThumbnailData>
  const { data } = useQuery(query, {
    variables: { id: videoId, ...leadThumbnailImage },
    skip: !videoId,
  })

  return data?.VideoDetail?.thumbnail?.custom?.image?.src
}
